import { Autocomplete, TextField } from '@mui/material'
import { useTheme } from 'react-admin'

const MOBILE = '@media (max-width: 599.95px)'

// Reusable sx for any MUI TextField or Autocomplete
export const getInputSx = (isDark) => ({
	'& .MuiOutlinedInput-root': {
		borderRadius: '8px',
		backgroundColor: isDark ? '#1e1e1e' : '#fff',
		transition: 'all 150ms ease',
		[MOBILE]: { minHeight: 44 },
		'& fieldset': {
			borderColor: isDark ? '#444' : '#e0e0e0',
			transition: 'border-color 150ms ease',
		},
		'&:hover fieldset': {
			borderColor: isDark ? '#5eaca0' : '#2D8C7A',
		},
		'&.Mui-focused fieldset': {
			borderColor: '#2D8C7A',
			borderWidth: '2px',
			boxShadow: '0 0 0 3px rgba(45,140,122,0.1)',
		},
	},
	'& .MuiInputLabel-root': {
		fontSize: '0.8rem',
		color: isDark ? '#888' : '#999',
		[MOBILE]: { fontSize: '0.9rem' },
		'&.Mui-focused': {
			color: '#2D8C7A',
			fontWeight: 600,
		},
	},
	'& .MuiInputBase-input': {
		fontSize: '0.85rem',
		[MOBILE]: { fontSize: '.9rem' },
	},
})

// Reusable sx for EditDialog / CreateDialog wrappers
export const getDialogSx = (isDark) => ({
	'& .MuiDialog-paper': {
		borderRadius: '12px',
		maxHeight: '90vh',
		backgroundColor: isDark ? '#111' : '#F3F4F6',
		[MOBILE]: {
			borderRadius: '8px',
			margin: '16px',
			width: 'calc(100% - 32px)',
		},
	},
	'& .MuiDialogTitle-root': {
		background: isDark
			? 'linear-gradient(90deg, #1a2a2a 0%, #1a1a1a 100%)'
			: 'linear-gradient(90deg, #daeeed 0%, #f0fdf9 40%, #fff 100%)',
		color: isDark ? '#ddd' : '#333',
		borderBottom: `1px solid ${isDark ? '#333' : '#c2e0df'}`,
		fontWeight: 700,
		py: 1.5,
		[MOBILE]: { py: 2 },
	},
	'& .MuiDialogContent-root': {
		[MOBILE]: { px: 2 },
	},
	'& .MuiDialogActions-root': {
		[MOBILE]: { px: 2, py: 1.5 },
	},
  '&& .RaTabbedShowLayout-content': {
    [MOBILE]: { padding: '5px' },
  },
  '& .MuiDialogTitle-root.MuiTypography-root': {
    [MOBILE]: { fontSize: '0.8rem' },
  },
  '& .MuiTab-root': {                                    
    [MOBILE]: {
      width: '120px',
      '&::before': { left: '60px' },
    },
  },
	...getInputSx(isDark),
})

// Dropdown paper props for Autocomplete
export const getDropdownPaperSx = (zIndex) => ({
	borderRadius: '8px',
	mt: 0.5,
	boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
	...(zIndex ? { zIndex } : {}),
})

// Styled TextField
export const StyledTextField = ({ sx, ...props }) => {
	const [theme] = useTheme()
	const isDark = theme === 'dark'

	return (
		<TextField
			size="small"
			{...props}
			sx={{ ...getInputSx(isDark), ...sx }}
		/>
	)
}

// Styled Autocomplete
export const StyledAutocomplete = ({ sx, slotProps, ...props }) => {
	const [theme] = useTheme()
	const isDark = theme === 'dark'
	const paperSx = slotProps?.paper?.sx || {}
	const zIndex = paperSx.zIndex

	return (
		<Autocomplete
			size="small"
			disablePortal={false}
			{...props}
			sx={{ ...getInputSx(isDark), ...sx }}
			slotProps={{
				...slotProps,
				paper: {
					...slotProps?.paper,
					sx: { ...getDropdownPaperSx(zIndex), ...paperSx },
				},
			}}
			renderInput={props.renderInput || ((params) => (
				<TextField {...params} label={props.label} size="small" />
			))}
		/>
	)
}
